import { api } from '@/lib/api';
import {
  INACTIVE_STATUS_CODES,
  type InactiveSensor,
  type InactiveSensorsFilters,
  type InactiveSensorsResponse,
  type InactiveSensorsSummary,
  type FleetSummary,
  type OrganisationOption,
  type SensorDetail,
} from './types';

// =============================================================================
// V4 API Response Types (from PostgreSQL)
// =============================================================================

interface V4Sensor {
  id: string;
  mongoSensorId: string;
  name: string;
  status: number;
  billable: boolean;
  lastReadingAt: string | null;
  sensorType: string | null;
  account: { id: string; name: string; accountNumber: string } | null;
  site: { id: string; name: string } | null;
  location: { id: string; name: string } | null;
  streamName: string | null;
  quoteNumbers: string[];
  createdAt: string;
  updatedAt: string;
}

interface V4SensorsResponse {
  success: boolean;
  sensors: V4Sensor[];
  total: number;
  pageNumber: number;
  pageLength: number;
}

interface V4SummaryResponse {
  success: boolean;
  summary: {
    total: number;
    unmanaged: number;
    inactive: number;
    maintenance: number;
    archived: number;
  };
}

interface V4FleetSummaryResponse {
  success: boolean;
  summary: {
    total: number;
    active: number;
    inactive: number;
    maintenance: number;
    inhibited: number;
  };
}

interface V4AccountsResponse {
  accounts: Array<{ id: string; name: string; accountNumber: string }>;
  total: number;
  pageNumber: number;
  pageLength: number;
}

// =============================================================================
// Transform Functions
// =============================================================================

function transformV4Sensor(v4: V4Sensor): InactiveSensor {
  return {
    _id: v4.mongoSensorId,
    name: v4.name,
    status: v4.status === 5 ? v4.status : INACTIVE_STATUS_CODES[0],
    timestamp: v4.lastReadingAt,
    billable: v4.billable,
    organisation: v4.account
      ? { _id: v4.account.id, name: v4.account.name }
      : null,
    site: v4.site ? { _id: v4.site.id, name: v4.site.name } : null,
    location: v4.location ? { _id: v4.location.id, name: v4.location.name } : null,
    sensortype: v4.sensorType ? { _id: '', name: v4.sensorType } : null,
    stream: v4.streamName ? { _id: '', name: v4.streamName } : null,
    quoteNumbers: v4.quoteNumbers || [],
    createdAt: v4.createdAt,
    updatedAt: v4.updatedAt,
  };
}

// Internal ATEK accounts excluded from operations views
const EXCLUDED_ACCOUNT_NAMES = [
  '_ATEK_',
  '_ATEK_RnD_',
  'DEMO Dynacare',
  'DEMO CIC-TR TO DELETE',
  'DEMO DEMO',
  'DEMO Diex Recherche',
  'LABMED Solutions',
  'DÉMO PARAZA PHARMA',
  'HAKIM HOME',
];

// =============================================================================
// API Functions
// =============================================================================

/**
 * Get inactive sensors with filtering and pagination
 * Uses V4 API which returns data and count in a single request
 */
export async function getInactiveSensors(
  filters: InactiveSensorsFilters = {}
): Promise<InactiveSensorsResponse> {
  const params: Record<string, string | number> = {};

  // Exclude internal ATEK accounts
  params.excludeAccountNames = EXCLUDED_ACCOUNT_NAMES.join(',');

  // Inactive means status 5 only.
  params.status = filters.status ?? INACTIVE_STATUS_CODES[0];

  // Search filter
  if (filters.search) {
    params.search = filters.search;
  }

  // Account filter (V4 uses accountId, not organisation)
  if (filters.organisation_id) {
    params.accountId = filters.organisation_id;
  }

  // Pagination
  params.pageNumber = filters.page_number ?? 0;
  params.pageLength = filters.page_length ?? 50;

  // Sorting
  if (filters.sort_key) {
    // Map old sort keys to V4 column names
    const sortKeyMap: Record<string, string> = {
      timestamp: 'lastReadingAt',
      name: 'name',
      status: 'status',
      billable: 'billable',
    };
    params.sortKey = sortKeyMap[filters.sort_key] || filters.sort_key;
  }
  if (filters.sort_direction !== undefined) {
    params.sortDirection = filters.sort_direction;
  }

  const response = await api.get<V4SensorsResponse & { status?: boolean; msg?: string }>(
    '/v4/sensors/inactive',
    params,
  );

  // Permission gates answer HTTP 200 {status:false, msg} — surface that as an
  // error instead of a cryptic TypeError on the map below.
  if (!Array.isArray(response.sensors)) {
    throw new Error(response.msg || 'Unexpected /v4/sensors/inactive response: missing sensors list');
  }

  return {
    sensors: response.sensors.map(transformV4Sensor),
    total: response.total,
    page_number: response.pageNumber,
    page_length: response.pageLength,
  };
}

/**
 * Get summary counts by status
 * Uses V4 API which returns all counts in a single request
 */
export async function getInactiveSensorsSummary(): Promise<InactiveSensorsSummary> {
  const response = await api.get<V4SummaryResponse>('/v4/sensors/inactive/summary', {
    excludeAccountNames: EXCLUDED_ACCOUNT_NAMES.join(','),
  });

  return {
    total: response.summary.inactive,
    inactive: response.summary.inactive,
  };
}

/**
 * Get fleet-wide sensor summary counts (all statuses, not just inactive)
 */
export async function getFleetSummary(): Promise<FleetSummary> {
  const response = await api.get<V4FleetSummaryResponse>('/v4/sensors/fleet-summary');
  return response.summary;
}

/**
 * Get customer accounts for dropdown filter
 * Uses V4 API which returns PostgreSQL UUIDs compatible with V4 sensors endpoint.
 * Filters by type=customer to exclude suppliers, partners, prospects.
 * Paginates through all pages since V4 API caps at 100 per request.
 */
export async function getOrganisationsWithInactiveSensors(): Promise<OrganisationOption[]> {
  const PAGE_SIZE = 100;
  const allOrgs: OrganisationOption[] = [];
  let pageNumber = 0;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const response = await api.get<V4AccountsResponse>('/v4/accounts', {
      pageNumber,
      pageLength: PAGE_SIZE,
      sortKey: 'name',
      sortDirection: 1,
      type: 'customer',
    });

    const accounts = response.accounts || [];
    for (const account of accounts) {
      allOrgs.push({ id: account.id, name: account.name });
    }

    if (accounts.length < PAGE_SIZE) break;
    pageNumber++;
  }

  // Filter out internal ATEK accounts
  return allOrgs.filter(org => !EXCLUDED_ACCOUNT_NAMES.includes(org.name));
}

// =============================================================================
// Sensor Detail API
// =============================================================================

interface V4SensorDetailResponse {
  success: boolean;
  sensor: SensorDetail;
}

/**
 * Get full sensor detail by mongoSensorId
 */
export async function getSensorDetail(sensorId: string): Promise<SensorDetail> {
  const response = await api.get<V4SensorDetailResponse>(`/v4/sensors/${sensorId}`);
  return response.sensor;
}
